// The two wizard card illustrations (paw / kittens), inlined.
//
// They used to be <img src="/wizard/paw.svg">, which means the browser only STARTS fetching them once
// the card has rendered — and on the intake/release wizards that request queues behind the search
// results' photo fetches and (in dev) the module graph, so the cards sat empty for a beat every time
// the wizard opened. Importing the file with `?raw` puts the markup in the JS module itself: it is
// part of the same chunk as the component, so the drawing paints on the first render with no request
// of its own. The files stay real .svg assets (src/assets/wizard) so they can be swapped out as art.
import kittensMarkup from "../assets/wizard/kittens.svg?raw";
import pawMarkup from "../assets/wizard/paw.svg?raw";

/**
 * Prepare the file's markup for inlining: drop the XML prolog (an inline <svg> in HTML must not carry
 * one) and give it a viewBox.
 *
 * The viewBox matters: both files declare only width/height, which is fine for an <img> — the browser
 * scales the whole canvas — but an inline <svg> without a viewBox has no coordinate system to map to
 * its box, so the CSS max-height simply CLIPS the drawing (the paw came out as a green blob). Derive
 * it from the declared size and let the element scale to its container instead.
 */
function inlineSvg(markup: string): string {
  const svg = markup.replace(/^[\s\S]*?(?=<svg)/, "");
  if (/\sviewBox=/i.test(svg)) return svg;
  const width = svg.match(/\swidth="([\d.]+)"/i)?.[1];
  const height = svg.match(/\sheight="([\d.]+)"/i)?.[1];
  if (!width || !height) return svg;
  return svg.replace(
    /<svg\b/,
    `<svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet"`,
  );
}

const PAW = inlineSvg(pawMarkup);
const KITTENS = inlineSvg(kittensMarkup);

/** One animal (the paw print). Decorative — the card's own title carries the meaning. */
export function PawIllustration() {
  return (
    <span
      className="fawkes-wizard-illustration"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: PAW }}
    />
  );
}

/** Several animals (the three kittens). Decorative, like the paw. */
export function KittensIllustration() {
  return (
    <span
      className="fawkes-wizard-illustration"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: KITTENS }}
    />
  );
}
